import { useState } from "react";
import {
  ArrowUpRight,
  CalendarDays,
  Check,
  Copy,
  Linkedin,
  LoaderCircle,
  Mail,
  Send,
} from "lucide-react";
import { API, CALENDAR, EMAIL, FAQ_ITEMS, LINKEDIN } from "./data";
import { Magnetic, Reveal } from "./Reveal";

const EMPTY_INQUIRY = {
  name: "",
  email: "",
  company: "",
  project_type: "AI product / automation",
  timeline: "This month",
  brief: "",
};

export function Contact() {
  const [copied, setCopied] = useState(false);
  const [inquiry, setInquiry] = useState(EMPTY_INQUIRY);
  const [inquiryState, setInquiryState] = useState("idle");

  const copyEmail = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(EMAIL);
      } else {
        const helper = document.createElement("textarea");
        helper.value = EMAIL;
        helper.setAttribute("readonly", "");
        helper.style.position = "fixed";
        helper.style.opacity = "0";
        document.body.appendChild(helper);
        helper.select();
        document.execCommand("copy");
        helper.remove();
      }
    } catch (error) {
      window.location.href = `mailto:${EMAIL}`;
    } finally {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const submitInquiry = async (event) => {
    event.preventDefault();
    setInquiryState("sending");
    try {
      const response = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(inquiry),
      });
      if (!response.ok) throw new Error("Unable to save inquiry");
      setInquiryState("success");
      setInquiry(EMPTY_INQUIRY);
    } catch (error) {
      setInquiryState("error");
    }
  };

  return (
    <section className="contact page-width" id="contact" data-testid="contact-section">
      <Reveal className="contact-inner">
        <div>
          <div className="section-kicker">Have a hard problem?</div>
          <h2>
            Let’s make it
            <br />
            <em>move.</em>
          </h2>
          <p>
            If you’re building something ambitious — or trying to unstick something important — I’d
            love to hear the real version of it.
          </p>
        </div>
        <div className="contact-actions">
          <Magnetic>
            <a className="button button-light" href={CALENDAR} target="_blank" rel="noreferrer" data-testid="contact-book-call-button">
              <CalendarDays size={17} /> Book a working session <ArrowUpRight size={16} />
            </a>
          </Magnetic>
          <a className="contact-link" href={`mailto:${EMAIL}`} data-testid="contact-email-link">
            <Mail size={17} /> {EMAIL}
          </a>
          <button className="contact-link copy-button" onClick={copyEmail} data-testid="contact-copy-email-button">
            {copied ? <Check size={17} /> : <Copy size={17} />} {copied ? "Copied" : "Copy email"}
          </button>
          <a className="contact-link" href={LINKEDIN} target="_blank" rel="noreferrer" data-testid="contact-linkedin-link">
            <Linkedin size={17} /> LinkedIn <ArrowUpRight size={14} />
          </a>
        </div>
      </Reveal>
      <div className="inquiry-grid">
        <Reveal className="inquiry-panel" delay={0.05}>
          <div className="section-kicker">Start a conversation</div>
          <h3>Tell me what’s stuck.</h3>
          <p className="panel-intro">A few useful details is all I need to understand whether I can help.</p>
          {inquiryState === "success" ? (
            <div className="inquiry-success" data-testid="inquiry-success-message">
              <span className="success-icon">
                <Check size={18} />
              </span>
              <strong>Brief received.</strong>
              <p>Thanks — I’ve got the shape of it. I’ll review your note and get back to you soon.</p>
              <button className="text-link underline-link" onClick={() => setInquiryState("idle")} data-testid="inquiry-send-another-button">
                Send another brief <ArrowUpRight size={14} />
              </button>
            </div>
          ) : (
            <form onSubmit={submitInquiry} data-testid="inquiry-form">
              <div className="form-row">
                <label>
                  Name
                  <input
                    required
                    value={inquiry.name}
                    onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="inquiry-name-input"
                  />
                </label>
                <label>
                  Email
                  <input
                    required
                    type="email"
                    value={inquiry.email}
                    onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })}
                    placeholder="you@company.com"
                    data-testid="inquiry-email-input"
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Company <span>(optional)</span>
                  <input
                    value={inquiry.company}
                    onChange={(e) => setInquiry({ ...inquiry, company: e.target.value })}
                    placeholder="Company or project"
                    data-testid="inquiry-company-input"
                  />
                </label>
                <label>
                  What do you need?
                  <select
                    value={inquiry.project_type}
                    onChange={(e) => setInquiry({ ...inquiry, project_type: e.target.value })}
                    data-testid="inquiry-project-type-select"
                  >
                    <option>AI product / automation</option>
                    <option>Frontend architecture</option>
                    <option>Full-stack build</option>
                    <option>FDE / embedded engineering</option>
                    <option>Not sure yet</option>
                  </select>
                </label>
              </div>
              <label>
                Ideal timeline
                <select
                  value={inquiry.timeline}
                  onChange={(e) => setInquiry({ ...inquiry, timeline: e.target.value })}
                  data-testid="inquiry-timeline-select"
                >
                  <option>This month</option>
                  <option>Next 1–2 months</option>
                  <option>Exploring</option>
                  <option>Urgent</option>
                </select>
              </label>
              <label>
                What are you trying to make happen?
                <textarea
                  required
                  minLength={20}
                  value={inquiry.brief}
                  onChange={(e) => setInquiry({ ...inquiry, brief: e.target.value })}
                  placeholder="Tell me about the workflow, product, or problem..."
                  rows="4"
                  data-testid="inquiry-brief-input"
                />
              </label>
              {inquiryState === "error" && (
                <p className="form-error" data-testid="inquiry-error-message">
                  Something went wrong saving this brief. Please try again or email me directly.
                </p>
              )}
              <button
                className="button button-light form-submit"
                type="submit"
                disabled={inquiryState === "sending"}
                data-testid="inquiry-submit-button"
              >
                {inquiryState === "sending" ? (
                  <>
                    <LoaderCircle className="spin" size={16} /> Saving brief...
                  </>
                ) : (
                  <>
                    <Send size={16} /> Send project brief
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>
        <Reveal className="faq-panel" delay={0.12}>
          <div className="section-kicker">Founder FAQ</div>
          <h3>Before we talk.</h3>
          <div className="faq-list">
            {FAQ_ITEMS.map(([question, answer], index) => (
              <details key={question} data-testid={`faq-item-${index}`}>
                <summary>
                  {question}
                  <span>+</span>
                </summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
